import React, { useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CheckoutForm from './CheckoutForm';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { AccountBalance, CreditCard } from '@mui/icons-material';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PageContainer = styled.div`
  background-color: var(--color-bg);
  min-height: 100vh;
  padding: 80px 24px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-family: var(--font-serif);
  font-size: clamp(2.5rem, 5vw, 4rem);
  text-align: center;
  margin-bottom: 60px;
  color: var(--color-text);
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background: white;
  padding: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.1);
  height: fit-content;
`;

const CardTitle = styled.h3`
  font-family: var(--font-sans);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--color-primary-blue);
`;

const AmountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
`;

const AmountButton = styled.button`
  padding: 16px;
  background: ${props => props.selected ? 'var(--color-primary-blue)' : 'white'};
  color: ${props => props.selected ? 'white' : 'var(--color-text)'};
  border: 1px solid ${props => props.selected ? 'var(--color-primary-blue)' : 'rgba(0,0,0,0.2)'};
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    border-color: var(--color-primary-blue);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 16px;
  border: 1px solid rgba(0,0,0,0.2);
  border-radius: 8px;
  font-size: 1rem;
  font-family: var(--font-sans);
  margin-bottom: 24px;
  outline: none;

  &:focus {
    border-color: var(--color-primary-blue);
  }
`;

const DonateButton = styled.button`
  width: 100%;
  padding: 16px;
  background: var(--color-primary-blue);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.2s;

  &:hover {
    background: var(--color-accent);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const BankDetails = styled.div`
  background: #f8fafc;
  padding: 24px;
  border-radius: 8px;
  line-height: 1.8;
  color: var(--color-text-light);
  
  strong {
    color: var(--color-text);
  }
`;

const ErrorMsg = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.9rem;
`;

const Donation = () => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [amount, setAmount] = useState(50);

  const createPaymentIntent = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const amountInCents = Math.round(amount * 100);
      localStorage.setItem('lastDonationAmount', amount);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amountInCents }),
      });
      const data = await res.json();
      setClientSecret(data.clientSecret);
    } catch (error) {
      setError('Unable to process payment. Please try again.');
      console.error('Error creating payment intent:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setAmount(0);
      return;
    }
    const newAmount = parseFloat(value);
    if (!isNaN(newAmount)) {
      setAmount(newAmount);
    }
  };

  const suggestedAmounts = [50, 100, 150, 200];

  return (
    <PageContainer>
      <ContentWrapper>
        <PageTitle>Support Our Mission</PageTitle>

        <LayoutGrid>
          {/* Card 1: Online Donation */}
          <Card>
            <CardTitle>
              <CreditCard /> Make a Donation
            </CardTitle>

            {error && <ErrorMsg>{error}</ErrorMsg>}

            {!clientSecret ? (
              <>
                <p style={{ marginBottom: '16px', color: 'var(--color-text-light)' }}>
                  Select an amount (CHF):
                </p>
                <AmountGrid>
                  {suggestedAmounts.map((amt) => (
                    <AmountButton
                      key={amt}
                      selected={amount === amt}
                      onClick={() => setAmount(amt)}
                    >
                      {amt}
                    </AmountButton>
                  ))}
                </AmountGrid>

                <p style={{ marginBottom: '8px', color: 'var(--color-text-light)' }}>
                  Or enter custom amount:
                </p>
                <Input
                  type="number"
                  placeholder="Custom Amount"
                  value={amount || ''}
                  onChange={handleAmountChange}
                  min="1"
                />

                <DonateButton
                  onClick={createPaymentIntent}
                  disabled={amount <= 0 || isLoading}
                >
                  {isLoading ? 'Processing...' : 'Continue to Payment'}
                </DonateButton>
              </>
            ) : (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <CheckoutForm amount={amount} />
              </Elements>
            )}
          </Card>

          {/* Card 2: Bank Transfer */}
          <Card>
            <CardTitle>
              <AccountBalance /> Direct Bank Transfer
            </CardTitle>

            <p style={{ marginBottom: '24px', color: 'var(--color-text-light)' }}>
              Prefer a direct transfer? Here are our bank details:
            </p>

            <BankDetails>
              <div><strong>Account Name:</strong> SHALOM</div>
              <div><strong>IBAN:</strong> CH93 0024 3243 6476 3401 U</div>
              <div><strong>Bank:</strong> UBS</div>
              <div><strong>Address:</strong> SHALOM, 1024 ECUBLENS VD</div>
              <div><strong>Reference:</strong> Donation</div>
            </BankDetails>

            <div style={{ marginTop: '40px', textAlign: 'center' }}>
              <p style={{ marginBottom: '16px', fontSize: '0.9rem', color: 'var(--color-text-light)' }}>
                Scan with banking app (Twint/QR-Bill)
              </p>
              <img
                src="/assets/qr-code.png"
                alt="QR Bill"
                style={{
                  maxWidth: '200px',
                  border: '1px solid #ddd',
                  borderRadius: '8px'
                }}
              />
            </div>
          </Card>
        </LayoutGrid>
      </ContentWrapper>
    </PageContainer>
  );
};

export default Donation;